import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    notifications: [],
    unreadCount: 0,
    total: 0,
    page: 0,          // last page loaded (0 = nothing yet)
    hasMore: false,   // more pages available on the server
    loading: false,
    loadingMore: false,
    panelOpen: false,
    settings: {
        likedTrackPlayed: true,
        favouriteArtistNewMix: true,
        favouriteArtistTrackPlayed: true,
        emailLikedTrackPlayed: false,
        emailFavouriteArtistNewMix: false,
        emailFavouriteArtistTrackPlayed: false,
    },
    settingsLoading: false,
    error: null,
};

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        // Dispatched on logout: the list, unread badge, AND the per-user
        // notification preferences all belong to the account, not the device.
        clearNotifications(state) {
            Object.assign(state, initialState);
        },
        // Replaces the list (page 1 / reset). Use appendNotifications for
        // subsequent pages so infinite scroll doesn't clobber what's shown.
        setNotifications(state, action) {
            const { data, total, page = 1 } = action.payload;
            state.notifications = data;
            state.total = total;
            state.page = data.length > 0 ? page : 0;
            state.hasMore = data.length < total;
        },
        appendNotifications(state, action) {
            const { data, total, page } = action.payload;
            const seen = new Set(state.notifications.map(n => n.id));
            const fresh = data.filter(n => !seen.has(n.id));
            state.notifications.push(...fresh);
            state.total = total;
            state.page = page;
            // Stop when the server has nothing more or (defensively) when a
            // page comes back all-dupes — otherwise the sentinel would loop.
            state.hasMore = data.length > 0 && state.notifications.length < total;
        },
        setUnreadCount(state, action) {
            state.unreadCount = action.payload;
        },
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setLoadingMore(state, action) {
            state.loadingMore = action.payload;
        },
        setPanelOpen(state, action) {
            state.panelOpen = action.payload;
        },
        togglePanel(state) {
            state.panelOpen = !state.panelOpen;
        },
        setSettings(state, action) {
            state.settings = action.payload;
        },
        setSettingsLoading(state, action) {
            state.settingsLoading = action.payload;
        },
        markRead(state, action) {
            const id = action.payload;
            const notif = state.notifications.find(n => n.id === id);
            if (notif && !notif.read) {
                notif.read = true;
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
        },
        markAllRead(state) {
            state.notifications.forEach(n => { n.read = true; });
            state.unreadCount = 0;
        },
        removeNotification(state, action) {
            const id = action.payload;
            const notif = state.notifications.find(n => n.id === id);
            state.notifications = state.notifications.filter(n => n.id !== id);
            state.total = Math.max(0, state.total - 1);
            if (notif && !notif.read) {
                state.unreadCount = Math.max(0, state.unreadCount - 1);
            }
        },
        setError(state, action) {
            state.error = action.payload;
        },
    },
});

export const {
    clearNotifications,
    setNotifications,
    appendNotifications,
    setUnreadCount,
    setLoading,
    setLoadingMore,
    setPanelOpen,
    togglePanel,
    setSettings,
    setSettingsLoading,
    markRead,
    markAllRead,
    removeNotification,
    setError,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
